import { useCallback, useMemo } from 'react';
import { annotate, documentLineFor, extractScript } from '../annotate.js';

/**
 * Shows the game's real code with plain-English notes beside it.
 *
 * Read-only for now: the point at this stage is that the code stops being magic.
 * Editing arrives with the Patch rung, where a child fixes a deliberate bug.
 */
/** Bring the line a slider controls into view without yanking the whole page. */
const scrollIntoView = (el: HTMLLIElement | null) =>
  el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

export function CodePanel({ html, focusLine }: { html: string; focusLine?: number | null }) {
  const { lines, notes, offset } = useMemo(() => {
    const script = extractScript(html);
    return {
      lines: script.code.split('\n'),
      offset: script.offset,
      notes: new Map(annotate(html).map((n) => [n.line, n.text])),
    };
  }, [html]);

  return (
    <div className="code">
      <h3>This is the actual code that makes your game work.</h3>
      <ol className="code__lines">
        {lines.map((line, i) => {
          const documentLine = documentLineFor(offset, i);
          const note = notes.get(documentLine);
          const focused = focusLine != null && documentLine === focusLine;
          const classes = [
            'code__line',
            note ? 'code__line--noted' : '',
            focused ? 'code__line--focus' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <li key={i} className={classes} ref={focused ? scrollIntoView : undefined}>
              <code>{line || ' '}</code>
              {note && <span className="code__note">{note}</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
